import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Trophy, Target, Users, Award, TrendingUp, Sparkles } from 'lucide-react';
import BadgeIcon from './BadgeIcon';
import { fmtDateNo } from '../utils/dateFormat';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const LEVEL_COLORS = {
  nybegynner: { bg: 'bg-orange-500/20', border: 'border-orange-500/50', text: 'text-orange-400', ring: '#f97316', label: 'Nybegynner' },
  viderekommende: { bg: 'bg-gray-500/20', border: 'border-gray-500/50', text: 'text-gray-300', ring: '#94a3b8', label: 'Viderekommende' },
  bronse: { bg: 'bg-amber-700/20', border: 'border-amber-700/50', text: 'text-amber-500', ring: '#b45309', label: 'Bronse' },
  solv: { bg: 'bg-slate-400/20', border: 'border-slate-400/50', text: 'text-slate-300', ring: '#cbd5e1', label: 'Sølv' },
  gull: { bg: 'bg-yellow-500/20', border: 'border-yellow-500/50', text: 'text-yellow-400', ring: '#eab308', label: 'Gull' },
};

const MerkeskytingStatsCard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get(`${API_BASE}/api/merkeskyting/stats`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setStats(r.data))
      .catch(() => setStats(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="bg-gray-800 rounded-lg p-6 text-gray-400">Laster merkeskyting-statistikk...</div>;
  if (!stats || stats.total_series === 0) {
    return (
      <div className="bg-gray-800 rounded-lg p-6 text-center border border-gray-700">
        <Trophy className="w-10 h-10 mx-auto mb-2 text-gray-600" />
        <p className="text-gray-400">Ingen merkeskyting-resultater ennå.</p>
        <p className="text-gray-500 text-sm mt-1">Så snart klubben registrerer resultater, dukker statistikken opp her.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="merkeskyting-stats-card">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-yellow-500/20 rounded-lg">
          <Trophy className="w-6 h-6 text-yellow-400" />
        </div>
        <div>
          <h3 className="text-xl font-bold text-white">Merkeskyting</h3>
          <p className="text-gray-400 text-sm">NSFs ferdighetsmerker – klubbens fremdrift</p>
        </div>
      </div>

      {/* KPI-kort */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-gradient-to-br from-blue-600/20 to-blue-800/20 border border-blue-500/30 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-1"><Target className="w-4 h-4 text-blue-400" /><p className="text-xs text-blue-300 font-medium">Serier registrert</p></div>
          <p className="text-3xl font-black text-white">{stats.total_series}</p>
        </div>
        <div className="bg-gradient-to-br from-emerald-600/20 to-emerald-800/20 border border-emerald-500/30 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-1"><Users className="w-4 h-4 text-emerald-400" /><p className="text-xs text-emerald-300 font-medium">Deltakere</p></div>
          <p className="text-3xl font-black text-white">{stats.total_participants}</p>
        </div>
        <div className="bg-gradient-to-br from-yellow-600/20 to-yellow-800/20 border border-yellow-500/30 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-1"><Award className="w-4 h-4 text-yellow-400" /><p className="text-xs text-yellow-300 font-medium">Merker oppnådd</p></div>
          <p className="text-3xl font-black text-white">{stats.total_awards}</p>
        </div>
        <div className="bg-gradient-to-br from-purple-600/20 to-purple-800/20 border border-purple-500/30 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-1"><Sparkles className="w-4 h-4 text-purple-400" /><p className="text-xs text-purple-300 font-medium">Nye siste 30 dager</p></div>
          <p className="text-3xl font-black text-white">{stats.awards_last_30_days}</p>
        </div>
      </div>

      {/* Merker per nivå */}
      <div className="bg-gray-800 rounded-xl p-5 border border-gray-700">
        <h4 className="text-white font-bold mb-3">Fordeling per nivå</h4>
        <div className="grid grid-cols-5 gap-2">
          {(stats.levels_meta || []).map((lv) => {
            const c = LEVEL_COLORS[lv.key] || {};
            const count = stats.level_counts?.[lv.key] || 0;
            return (
              <div key={lv.key} className={`${c.bg} border ${c.border} rounded-xl p-3 text-center`}>
                <BadgeIcon level={lv.key} size={44} className="mx-auto mb-2" />
                <p className={`text-xs font-bold ${c.text}`}>{c.label}</p>
                <p className="text-2xl font-black text-white">{count}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Topp 10 skyttere */}
      {stats.top_shooters?.length > 0 && (
        <div className="bg-gray-800 rounded-xl p-5 border border-gray-700">
          <h4 className="text-white font-bold mb-3 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-yellow-400" /> Topp 10 aktive merkeskyttere
          </h4>
          <div className="space-y-1">
            {stats.top_shooters.map((s, i) => (
              <div key={s.member_id} className="flex items-center gap-3 py-1.5 border-b border-gray-700 last:border-0">
                <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${i < 3 ? 'bg-yellow-500 text-gray-900' : 'bg-gray-700 text-gray-300'}`}>
                  {i + 1}
                </span>
                <span className="flex-1 text-white text-sm truncate">{s.name}</span>
                <span className="text-yellow-400 font-bold text-sm">{s.count} serier</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Nyeste merker */}
      {stats.recent_awards?.length > 0 && (
        <div className="bg-gray-800 rounded-xl p-5 border border-gray-700">
          <h4 className="text-white font-bold mb-3">🏆 Nyeste merker</h4>
          <div className="space-y-2">
            {stats.recent_awards.map((a, i) => {
              const c = LEVEL_COLORS[a.level] || {};
              return (
                <div key={i} className="flex items-center gap-3 p-2 rounded-lg bg-gray-700/50">
                  <BadgeIcon level={a.level} size={32} className="flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-medium truncate">{a.member_name}</p>
                    <p className={`text-xs ${c.text}`}>{c.label} – {a.level_name}</p>
                  </div>
                  <span className="text-xs text-gray-500 whitespace-nowrap">{fmtDateNo(a.achieved_at)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default MerkeskytingStatsCard;
